import HttpCommunity from '@/services/httpCommunity'
import HttpTV from '@/services/httpTV'
import HttpJobs from '@/services/httpJobs'
import HttpMain from '@/services/http'
import HttpAgency from '@/services/httpAgency'

const PER_PAGE = 10
const COMMUNITY_PER_PAGE = 15

// ── Community API ───────────────────────────────────
// Main search (all types at once, now configurable per_page)
export const globalSearch = query =>
	HttpCommunity.get(`/search?q=${encodeURIComponent(query)}&per_page=${COMMUNITY_PER_PAGE}`, { baseURL: 'locale' })

// Individual community type searches (for load more)
const COMMUNITY_SEARCH = {
	games: (query, page) => HttpCommunity.get(`/games/search?q=${encodeURIComponent(query)}&page=${page}&per_page=${PER_PAGE}`, { baseURL: 'locale' }),
	characters: (query, page) => HttpCommunity.get(`/characters/search?q=${encodeURIComponent(query)}&page=${page}&per_page=${PER_PAGE}`, { baseURL: 'locale' }),
	companies: (query, page) => HttpCommunity.get(`/companies/search?q=${encodeURIComponent(query)}&page=${page}&per_page=${PER_PAGE}`, { baseURL: 'locale' }),
	platforms: (query, page) => HttpCommunity.get(`/platforms/search?q=${encodeURIComponent(query)}&page=${page}&per_page=${PER_PAGE}`, { baseURL: 'locale' }),
	pulses: (query, page) => HttpCommunity.get(`/pulses/search?q=${encodeURIComponent(query)}&page=${page}&per_page=${PER_PAGE}`, { baseURL: 'locale' }),
	reviews: (query, page) => HttpCommunity.get(`/reviews/search?q=${encodeURIComponent(query)}&page=${page}&per_page=${PER_PAGE}`, { baseURL: 'locale' }),
	videos: (query, page) => HttpCommunity.get(`/videos/search?q=${encodeURIComponent(query)}&page=${page}&per_page=${PER_PAGE}`, { baseURL: 'locale' }),
	events: (query, page) => HttpCommunity.get(`/events/search?q=${encodeURIComponent(query)}&page=${page}&per_page=${PER_PAGE}`, { baseURL: 'locale' }),
	quizzes: (query, page) => HttpCommunity.get(`/quizzes/search?q=${encodeURIComponent(query)}&page=${page}&per_page=${PER_PAGE}`, { baseURL: 'locale' }),
	users: (query, page) => HttpCommunity.get(`/users/search?q=${encodeURIComponent(query)}&page=${page}&per_page=${PER_PAGE}`, { baseURL: 'locale' }),
}

const COMMUNITY_TYPES = Object.keys(COMMUNITY_SEARCH)

// ── Other APIs ──────────────────────────────────────
const API_SEARCH = {
	streamers: (query, page) => HttpTV.get(`/search?filter[term]=${encodeURIComponent(query)}&page=${page}`),
	vacancies: (query, page) => HttpJobs.get(`/vacancy?filter[term]=${encodeURIComponent(query)}&per_page=${PER_PAGE}&page=${page}`),
	blog_posts: (query, page) => HttpMain.get(`/blog-posts?filter[term]=${encodeURIComponent(query)}&per_page=${PER_PAGE}&page=${page}`),
	network_companies: (query, page) => HttpMain.get(`/company?filter[term]=${encodeURIComponent(query)}&per_page=${PER_PAGE}&page=${page}`),
	influencers: (query, page) => HttpAgency.get(`/influencers-professional-info?filter[term]=${encodeURIComponent(query)}&per_page=${PER_PAGE}&page=${page}`),
	cases: (query, page) => HttpAgency.get(`/marketing-cases?filter[term]=${encodeURIComponent(query)}&per_page=${PER_PAGE}&page=${page}`),
	showcases: (query, page) => HttpAgency.get(`/showcases?filter[term]=${encodeURIComponent(query)}&per_page=${PER_PAGE}&page=${page}`),
}

/**
 * Extract items and pagination from a Fractal-paginated response
 */
function extractPaginated(res) {
	const items = res?.data?.data || []
	const pagination = res?.data?.meta?.pagination || {}
	return {
		items,
		total: pagination.total || items.length,
		hasMore: (pagination.current_page || 1) < (pagination.total_pages || 1),
	}
}

/**
 * Extract items from community individual search (Laravel Resource collection)
 */
function extractCommunityPaginated(res) {
	const body = res?.data || {}
	// Community endpoints return { data: [...], meta: { current_page, last_page, total, per_page } }
	// or { data: [...], links: {...}, meta: {...} } (Laravel Resource pagination)
	const items = body.data || []
	const meta = body.meta || {}
	return {
		items,
		total: meta.total || items.length,
		hasMore: (meta.current_page || 1) < (meta.last_page || 1),
	}
}

/**
 * Search across all platforms in parallel
 * Returns: { results: { type: items[] }, pagination: { type: { total, hasMore, page } } }
 */
export const globalSearchAll = async query => {
	const apiTypes = Object.keys(API_SEARCH)
	const [communityRes, ...apiResults] = await Promise.allSettled([
		globalSearch(query),
		...apiTypes.map(type => API_SEARCH[type](query, 1)),
	])

	const results = {}
	const pagination = {}

	// Community results (grouped by type)
	if (communityRes.status === 'fulfilled') {
		const data = communityRes.value?.data?.data || {}
		for (const type of COMMUNITY_TYPES) {
			if (data[type] && data[type].length) {
				results[type] = data[type]
				// Community mainSearch returns up to COMMUNITY_PER_PAGE per type
				// Has more if count equals per_page (likely truncated)
				const count = data[type].length
				pagination[type] = {
					total: count,
					hasMore: count >= COMMUNITY_PER_PAGE,
					page: 1,
				}
			}
		}
	}

	// Other API results (paginated with Fractal)
	apiTypes.forEach((type, i) => {
		if (apiResults[i].status === 'fulfilled') {
			let extracted
			if (type === 'streamers') {
				// TV API returns flat array with type field, no pagination
				const items = (apiResults[i].value?.data?.data || []).filter(it => it.type === 'streamer')
				extracted = { items, total: items.length, hasMore: false }
			} else {
				extracted = extractPaginated(apiResults[i].value)
			}
			if (extracted.items.length) {
				results[type] = extracted.items
				pagination[type] = { total: extracted.total, hasMore: extracted.hasMore, page: 1 }
			}
		}
	})

	return { results, pagination }
}

/**
 * Load more results for a specific type
 * Returns: { items, total, hasMore }
 */
export const loadMoreResults = async (type, query, page) => {
	// Community types — use individual search endpoints
	if (COMMUNITY_SEARCH[type]) {
		const res = await COMMUNITY_SEARCH[type](query, page)
		return extractCommunityPaginated(res)
	}

	// Other APIs
	if (API_SEARCH[type]) {
		if (type === 'streamers') {
			return { items: [], total: 0, hasMore: false }
		}
		const res = await API_SEARCH[type](query, page)
		return extractPaginated(res)
	}

	return { items: [], total: 0, hasMore: false }
}
